import {
  createRouter,
  createWebHashHistory,
  RouteRecordRaw,
} from 'vue-router';
import { unauthorizedRoutes } from './unauthorizedRoutes';
import { authorizedRoutes } from './authorizedRoutes';
import { publicRoutes } from './publicRoutes';

import { loadLayout } from './middleware/loadLayout';
import { setPageTitle } from './middleware/setPageTitle';
import { checkPermissions } from './middleware/checkPermissions';
import { handleAlwaysAvailableRoute } from './middleware/alwaysAvailableRoute';
import {
  checkUnauthorizedRoute,
  handleUnauthorizedRoute,
} from './middleware/unauthorizedRoute';
import {
  setRedirectAfterLoginRoute,
  setLastProtectedRoute,
} from './middleware/redirectAfterLogin';

import { getItemFromLocalStorage } from '@core/utils/persistenceStorage';
import { useBaseStore } from '~/stores/base';
import { usePermissionsStore } from '~/stores/permissions';
import { useProjectSettingsStore } from '@core/stores/projectSettings';
import { getCurrentBase } from '~/helpers/persistenceBase';

const routes: Array<RouteRecordRaw> = [
  ...unauthorizedRoutes,
  ...authorizedRoutes,
  ...publicRoutes,
];

const router = createRouter({
  history: createWebHashHistory(process.env.BASE_URL),
  routes,
});

let previousPath: null | object = null;

router.beforeEach(async (to, from, next) => {
  if (from.name !== to.name) {
    previousPath = { ...from };
  }

  const { getBaseInfo, baseId } = useBaseStore();
  const { getCurrentUserPermissions } = usePermissionsStore();
  const projectSettingsStore = useProjectSettingsStore();

  projectSettingsStore.setPageTitle('');

  await loadLayout(to);
  setPageTitle();

  const token: string | null = getItemFromLocalStorage('accessToken');
  
  const routeBase = typeof to.params.base === 'string' ? to.params.base : null;
  const savedBase = getCurrentBase();
  const base = routeBase || savedBase;

  if (handleAlwaysAvailableRoute(to, next)) {
    return;
  }

  const isUnauthorizedRoute = checkUnauthorizedRoute(to);
  const isSelectBaseRoute = to.name === 'SelectBase';
  const isLoginRoute = to.name === 'Login';

  // Запоминаем любой защищённый маршрут заранее.
  // Это покрывает случай, когда токен пропадает уже после открытия страницы.
  if (!isUnauthorizedRoute && !isSelectBaseRoute && !isLoginRoute) {
    setLastProtectedRoute(to);
  }

  if (!isUnauthorizedRoute && !token) {
    if (!isLoginRoute) {
      setRedirectAfterLoginRoute(to);
    }

    return next({ name: 'Login' });
  }

  if (handleUnauthorizedRoute(to, token, base, next)) {
    return;
  }

  if (!isUnauthorizedRoute && token && !base && !isSelectBaseRoute) {
    setRedirectAfterLoginRoute(to);
    
    return next({ name: 'SelectBase' });
  }

  const shouldLoadPermissions =
    !isUnauthorizedRoute &&
    !!base &&
    !baseId &&
    !isSelectBaseRoute;

  if (shouldLoadPermissions) {
    await getBaseInfo();
    await getCurrentUserPermissions();
  }

  checkPermissions(to, next);
});

export const getPreviousFullPath = () => previousPath;

export default router;